import { useEffect, useRef, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { Flag, LANGUAGES, type Language } from './Flags';

/**
 * Language / country selector (desktop). The trigger shows the current flag; the
 * expanded panel is a white rounded card listing the supported countries with
 * their flags. Closes on outside click or Escape.
 */
export default function LanguageSelector() {
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<Language>(LANGUAGES[0]);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onPointer = (e: PointerEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('pointerdown', onPointer);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('pointerdown', onPointer);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                aria-label="Select language"
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
                className="flex items-center gap-1 text-white"
            >
                <Flag code={selected.code} className="h-4 w-6" />
                <FiChevronDown
                    className={`h-5 w-5 text-[#ff6600] transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
                />
            </button>

            {open && (
                <div
                    role="listbox"
                    className="absolute right-0 top-full z-10 mt-3 w-[210px] origin-top-right rounded-2xl bg-white p-2 shadow-[0_24px_60px_rgba(0,0,0,0.18)] ring-1 ring-black/5"
                >
                    {LANGUAGES.map((lang) => (
                        <button
                            key={lang.code}
                            type="button"
                            role="option"
                            aria-selected={lang.code === selected.code}
                            onClick={() => {
                                setSelected(lang);
                                setOpen(false);
                            }}
                            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-[#f5f5f5]"
                        >
                            <Flag code={lang.code} className="h-4 w-6" />
                            <span className="text-[15px] font-medium leading-6 text-[#171717]">{lang.name}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
